import { useMemo, useState, useEffect } from "react";
import {
  AppShell,
  Burger,
  Button,
  Collapse,
  Divider,
  Flex,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import {
  IconChevronDown,
  IconChevronUp,
  IconFilter,
  IconMailForward,
  IconUserPlus,
} from "@tabler/icons-react";
import Header from "@components/Tax Pro Only/Header/Header";
import Sidebar from "@components/Sidebar/Sidebar";
import "./TaxProClientList.scss";
import EmailModal from "@components/Tax Pro Only/Modals/EmailModal/EmailModal";
import Can from "../../Can";
import ArchiveToggle from "./components/ArchiveToggle/ArchiveToggle";
import TaxYearFilter from "./components/TaxYearFilter/TaxYearFilter";
import ClientListSearch from "./components/ClientListSearch/ClientListSearch";
import { useStatusStore } from "@utils/stores/useStatusStore";
import { useAssignee } from "@utils/hooks/useAssignee";
import { useAuthStore } from "@utils/stores/authStore";
import StatusFilterChips from "@pages/TaxProClientList/components/advancedFilters/StatusFilterChips/StatusFilterChips";
import AssigneeFilterChips from "@pages/TaxProClientList/components/advancedFilters/AssigneeFilterChips/AssigneeFilterChips";
import OrganizerStatusFilterChips from "@pages/TaxProClientList/components/advancedFilters/OrganizerStatusFilterChips/OrganizerStatusFilterChips";
import TagFilterChips from "@pages/TaxProClientList/components/advancedFilters/TagFilterChips/TagFilterChips";
import FilterSectionLabel from "@pages/TaxProClientList/components/advancedFilters/FilterSectionLabel/FilterSectionLabel";

import ClientListTable from "@/pages/TaxProClientList/components/ClientListTable/ClientListTable";
import InviteClientsModal from "@components/Tax Pro Only/Modals/InviteClientsModal/InviteClientsModal";
import BulkClientModal from "@/pages/TaxProClientList/components/BulkClientModal/BulkClientModal";
import { ClientListErrorBoundary } from "./components/ErrorBoundary";
import { useClientSelectionStore } from "@/utils/stores/useClientSelectionStore";

const TaxProClientList = () => {
  // Subscribe to selection state to show/hide filters
  // Only subscribe to the size to avoid creating new arrays on every render
  const selectedCount = useClientSelectionStore(
    (state) => state.selectedClientIds.size
  );
  // Disclosure hooks for modals
  const [opened, { toggle }] = useDisclosure(); // used to mobile
  const [openedModal, { open: openClient, close }] = useDisclosure(false);
  const [openedBulk, { open: openBulk, close: closeBulk }] =
    useDisclosure(false);
  const [openedEmail, { open: openEmail, close: closeEmail }] =
    useDisclosure(false);
  const {
    availableTaxYears,
    clickedStatus,
    clickedAssignees,
    clickedOrganizerStatus,
  } = useStatusStore();
  const { isOwner, isTeamLead } = useAuthStore();

  // State for advanced filters collapse - persisted to local storage
  const [advancedFiltersOpened, setAdvancedFiltersOpened] = useState(() => {
    const saved = localStorage.getItem("advancedFiltersOpened");
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Check if any filters are active (excluding tax year filter)
  // Team members can't see assignee filter, so don't check clickedAssignees for them
  const canFilterByAssignee = isOwner() || isTeamLead();
  const hasActiveFilters =
    (clickedStatus !== null && clickedStatus !== "") ||
    (canFilterByAssignee && clickedAssignees && clickedAssignees.length > 0) ||
    (clickedOrganizerStatus !== null && clickedOrganizerStatus !== "");

  // Auto-open Advanced Filters if any filter is active
  useEffect(() => {
    if (hasActiveFilters && !advancedFiltersOpened) {
      setAdvancedFiltersOpened(true);
    }
  }, [hasActiveFilters, advancedFiltersOpened]);

  // Persist advanced filters state to local storage
  useEffect(() => {
    localStorage.setItem(
      "advancedFiltersOpened",
      JSON.stringify(advancedFiltersOpened)
    );
  }, [advancedFiltersOpened]);

  // Handle toggle with notification if trying to close with active filters
  const handleAdvancedFiltersToggle = () => {
    console.log("[DEBUG] Toggle clicked", {
      advancedFiltersOpened,
      hasActiveFilters,
      clickedStatus,
      clickedAssignees,
      clickedOrganizerStatus,
    });

    // If trying to close with active filters, show notification
    if (advancedFiltersOpened && hasActiveFilters) {
      console.log("[DEBUG] Preventing close - building active filter names");
      const activeFilterNames: string[] = [];
      if (clickedStatus) activeFilterNames.push(`Status: ${clickedStatus}`);
      if (
        canFilterByAssignee &&
        clickedAssignees &&
        clickedAssignees.length > 0
      ) {
        const assigneeNames = clickedAssignees
          .map((id) => {
            const member = members?.find((m) => m.userId === id);
            return member ? `${member.firstName} ${member.lastName}` : id;
          })
          .join(", ");
        activeFilterNames.push(`Assignee: ${assigneeNames}`);
      }
      if (clickedOrganizerStatus)
        activeFilterNames.push(`Organizer Status: ${clickedOrganizerStatus}`);

      console.log("[DEBUG] Active filter names:", activeFilterNames);
      console.log("[DEBUG] Calling showNotification");

      showNotification({
        title: "Cannot Close Advanced Filters",
        message: `Clear these filters first: ${activeFilterNames.join(", ")}`,
        color: "blue",
      });

      console.log("[DEBUG] showNotification called, returning early");
      return;
    }

    console.log("[DEBUG] Toggling filters normally");
    setAdvancedFiltersOpened(!advancedFiltersOpened);
  };

  // Hoist useAssignee to parent to avoid re-fetching on Filter dropdown open/close
  const { members, isLoadingMembers } = useAssignee();

  // Memoize heavy modal components to prevent unnecessary re-renders
  const bulkModal = useMemo(
    () => <BulkClientModal opened={openedBulk} onClose={closeBulk} />,
    [openedBulk, closeBulk]
  );

  const emailModal = useMemo(
    () => (
      <EmailModal
        emailOpened={openedEmail}
        closeEmail={closeEmail}
        source="clientList"
      />
    ),
    [openedEmail, closeEmail]
  );

  const inviteClientsModal = useMemo(
    () => <InviteClientsModal openedModal={openedModal} close={close} />,
    [openedModal, close]
  );

  return (
    <AppShell
      className="client-page"
      header={{ height: 87 }}
      navbar={{
        width: "15.375rem",
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header className="app-header">
        <Burger
          className="burger"
          opened={opened}
          onClick={toggle}
          hiddenFrom="sm"
          size="sm"
        />
        <Header />
      </AppShell.Header>
      <AppShell.Navbar className="nav">
        <Sidebar />
      </AppShell.Navbar>
      <AppShell.Main className="main">
        <div className="main-content">
          {/* Header Section */}
          <div className="header-content">
            <h2 className="title">Clients</h2>
            <div className="header-buttons">
              <Can I="bulkImport" a="Clients">
                <Button
                  className="bulk-import-btn"
                  onClick={openBulk}
                  variant="outline"
                  bg="white"
                  leftSection={<IconUserPlus />}
                  size="md"
                >
                  Import Clients
                </Button>
              </Can>
              <Button
                className="bulk-import-btn"
                onClick={openEmail}
                leftSection={<IconMailForward />}
                size="md"
              >
                Bulk Email
              </Button>
            </div>
          </div>

          {/* Search Section */}
          <Flex className="sub-header-content" mt="md">
            <ClientListSearch />
          </Flex>

          {/* Conditionally show Filter Sections when no selection */}
          {selectedCount === 0 && (
            /* Filter Sections with Titles - shown when no selection */
            <Flex direction="column" gap="md" mt="lg" mb="lg">
              {/* Advanced Filters Collapsible Section */}
              <div>
                <Button
                  variant="subtle"
                  onClick={handleAdvancedFiltersToggle}
                  leftSection={<IconFilter size={18} />}
                  rightSection={
                    advancedFiltersOpened ? (
                      <IconChevronUp size={16} />
                    ) : (
                      <IconChevronDown size={16} />
                    )
                  }
                  styles={{
                    root: {
                      paddingLeft: 0,
                      paddingRight: 0,
                      fontWeight: 600,
                      fontSize: "14px",
                      color: hasActiveFilters ? "#0077CC" : "#00477A",
                      backgroundColor: hasActiveFilters
                        ? "#E7F5FF"
                        : "transparent",
                      paddingTop: hasActiveFilters ? "6px" : undefined,
                      paddingBottom: hasActiveFilters ? "6px" : undefined,
                      borderRadius: hasActiveFilters ? "8px" : undefined,
                    },
                  }}
                >
                  Advanced Filters
                </Button>

                <Collapse in={advancedFiltersOpened}>
                  <Flex direction="column" gap="lg" mt="md">
                    {/* Status Filter Section */}
                    <div>
                      <FilterSectionLabel>Status</FilterSectionLabel>
                      <StatusFilterChips />
                    </div>

                    <Divider />

                    {/* Organizer Status Filter Section */}
                    <div>
                      <FilterSectionLabel>Organizer Status</FilterSectionLabel>
                      <OrganizerStatusFilterChips />
                    </div>

                    <Divider />

                    {/* Assignee Filter Section - Only visible to owners and team leads */}
                    <Can I="filter" a="Clients">
                      <div>
                        <FilterSectionLabel>Assignee</FilterSectionLabel>
                        <AssigneeFilterChips
                          members={members}
                          isLoadingMembers={isLoadingMembers}
                        />
                      </div>

                      <Divider />
                    </Can>

                    {/* Tag Filter Section */}
                    <div>
                      <FilterSectionLabel>Tags</FilterSectionLabel>
                      <TagFilterChips />
                    </div>
                  </Flex>
                </Collapse>
              </div>

              {/* Tax Year and Archive Section */}
              <div>
                <FilterSectionLabel>Tax Year</FilterSectionLabel>
                <Flex gap="xl" align="center" wrap="wrap">
                  <TaxYearFilter
                    availableTaxYears={availableTaxYears || []}
                    isLoading={false}
                  />
                  <div style={{ marginLeft: "auto" }}>
                    <ArchiveToggle />
                  </div>
                </Flex>
              </div>
            </Flex>
          )}

          {/* Clients List Section */}
          <ClientListErrorBoundary>
            <ClientListTable onInviteClick={openClient} />
          </ClientListErrorBoundary>
        </div>
      </AppShell.Main>

      {/* Modals */}
      {bulkModal}
      {emailModal}
      {inviteClientsModal}
    </AppShell>
  );
};

export default TaxProClientList;
